import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { enUnidad, type Ciclo, type Cotizacion, type Generador, type Pesaje } from './contrato';

export function RegistroDePesaje({
  ciclo,
  alRegistrar,
}: {
  ciclo: Ciclo;
  alRegistrar: (p: Pesaje) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [generador, setGenerador] = useState<Generador | null>(null);
  const [fecha, setFecha] = useState(fechaInicial(ciclo.periodo));
  const [kilos, setKilos] = useState('');
  const [km, setKm] = useState('');
  const [guia, setGuia] = useState('');
  const [observacion, setObservacion] = useState('');
  const buscador = useRef<HTMLInputElement>(null);

  const buscado = useValorDiferido(busqueda.trim());
  const candidatos = useQuery({
    queryKey: ['generadores', 'para-pesar', buscado],
    enabled: generador === null && buscado.length >= 2,
    queryFn: () =>
      api.get<Pagina<Generador>>('/generadores', {
        query: { texto: buscado, activo: 'true', tamanio: 8 },
      }),
  });

  const peso = Number(kilos.replace(',', '.'));
  const kmEscritos = km.trim() === '' ? null : Number(km.replace(',', '.'));
  const pesoValido = Number.isFinite(peso) && peso > 0;
  const kilosDiferidos = useValorDiferido(pesoValido ? peso : 0);
  const kmDiferidos = useValorDiferido(kmEscritos);

  const cotizacion = useQuery({
    queryKey: ['generadores', 'cotizacion', ciclo.id, generador?.id, kilosDiferidos, kmDiferidos],
    enabled: generador !== null,
    queryFn: () =>
      api.get<Cotizacion>(`/generadores/ciclos/${ciclo.id}/cotizacion`, {
        query: {
          generador: generador!.id,
          kilos: kilosDiferidos,
          ...(kmDiferidos !== null && Number.isFinite(kmDiferidos) ? { km: kmDiferidos } : {}),
        },
      }),
    placeholderData: (anterior) => anterior,
  });

  const registrar = useMutation({
    mutationFn: () =>
      api.post<Pesaje>(`/generadores/ciclos/${ciclo.id}/pesajes`, {
        generadorId: generador!.id,
        fecha,
        kilos: peso,
        kmExcedentes: kmEscritos !== null && Number.isFinite(kmEscritos) ? kmEscritos : null,
        guia,
        observacion,
      }),
    onSuccess: (p) => {
      notificar.exito(`${p.generador} · $${p.total.toFixed(2)}`, 'Pesaje registrado');
      setGenerador(null);
      setBusqueda('');
      setKilos('');
      setKm('');
      setGuia('');
      setObservacion('');
      alRegistrar(p);
      buscador.current?.focus();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO REGISTRAR'),
  });

  const c = cotizacion.data;
  const listo = generador !== null && pesoValido && fecha !== '' && !registrar.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (listo) registrar.mutate();
      }}
      className="mb-5 rounded-2xl border border-armazon-200 bg-white p-5 shadow-sm"
    >
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        Registrar pesaje · {ciclo.periodo}
      </p>

      <div className="grid gap-3 lg:grid-cols-[minmax(16rem,1.4fr)_9rem_8rem_7rem_9rem]">
        <label className="relative grid gap-1.5">
          <span className="text-[10px] font-semibold uppercase text-armazon-600">Generador</span>
          {generador ? (
            <span className="flex h-10 items-center justify-between gap-2 rounded-lg border border-marca-300 bg-marca-50 px-3">
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold uppercase text-armazon-800">
                  {generador.nombre}
                  {generador.sucursal && ` (${generador.sucursal})`}
                </span>
                <span className="block truncate text-[10px] uppercase text-armazon-500">
                  {generador.literal}) {generador.categoria}
                </span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setGenerador(null);
                  setTimeout(() => buscador.current?.focus(), 0);
                }}
                aria-label="Cambiar de generador"
                className="text-armazon-400 hover:text-armazon-700"
              >
                <X className="size-4" aria-hidden />
              </button>
            </span>
          ) : (
            <span className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
                aria-hidden
              />
              <input
                ref={buscador}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Razón social, RUC o código…"
                autoFocus
                className={`${ENTRADA} pl-9`}
              />
            </span>
          )}
          {generador === null && buscado.length >= 2 && (
            <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-armazon-200 bg-white shadow-lg">
              {candidatos.isFetching && (
                <li className="px-3 py-2 text-xs uppercase text-armazon-400">Buscando…</li>
              )}
              {!candidatos.isFetching && (candidatos.data?.contenido.length ?? 0) === 0 && (
                <li className="px-3 py-2 text-xs uppercase text-armazon-400">
                  Ningún generador activo coincide
                </li>
              )}
              {candidatos.data?.contenido.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => setGenerador(g)}
                    className="flex w-full items-center justify-between gap-3 border-b border-armazon-100 px-3 py-2 text-left last:border-b-0 hover:bg-armazon-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-medium uppercase text-armazon-800">
                        {g.nombre}
                        {g.sucursal && ` (${g.sucursal})`}
                      </span>
                      <span className="importe block text-[10px] text-armazon-500">
                        {g.identificacion} · {g.codigo}
                      </span>
                    </span>
                    <span className="shrink-0 text-[10px] uppercase text-armazon-500">
                      {g.literal}) {g.categoria}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>

        <Campo etiqueta="Fecha">
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            min={`${ciclo.periodo}-01`}
            max={ultimoDia(ciclo.periodo)}
            className={`${ENTRADA} importe`}
          />
        </Campo>
        <Campo etiqueta="Peso (KG)">
          <input
            value={kilos}
            onChange={(e) => setKilos(e.target.value)}
            inputMode="decimal"
            className={`${ENTRADA} importe text-right`}
          />
        </Campo>
        <Campo etiqueta="Km en exceso">
          <input
            value={km}
            onChange={(e) => setKm(e.target.value)}
            inputMode="decimal"
            disabled={c ? !c.recargoPorDistancia : true}
            placeholder={c?.recargoPorDistancia ? String(c.kmExcedentes) : '—'}
            title={
              c?.recargoPorDistancia
                ? 'Vacío: los que tocan por su distancia'
                : 'Sin recargo en esta categoría'
            }
            className={`${ENTRADA} importe text-right disabled:bg-armazon-50`}
          />
        </Campo>
        <Campo etiqueta="Guía o ticket">
          <input
            value={guia}
            onChange={(e) => setGuia(e.target.value.toUpperCase())}
            className={`${ENTRADA} importe`}
          />
        </Campo>
      </div>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <input
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          placeholder="Observación (opcional)"
          className={`${ENTRADA} max-w-md flex-1`}
        />

        <div className="flex items-center gap-4">
          {generador && pesoValido && c && (
            <p className="text-right text-[11px] uppercase text-armazon-500">
              <span className="importe">
                {`${(c.unidad === 't' ? c.cantidad : peso).toLocaleString('es-EC', { maximumFractionDigits: 3 })} ${enUnidad(c.unidad)}`}{' '}
                × ${c.tarifaUnitaria.toFixed(2)}
                {c.recargo > 0 && ` + recargo $${c.recargo.toFixed(2)}`}
              </span>
              <span className="importe block text-lg font-bold text-armazon-900">
                ${c.total.toFixed(2)}
              </span>
            </p>
          )}
          <button
            type="submit"
            disabled={!listo}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-5 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:opacity-50"
          >
            {registrar.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Registrar
          </button>
        </div>
      </div>
    </form>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-semibold uppercase text-armazon-600">{etiqueta}</span>
      {children}
    </label>
  );
}

function fechaInicial(periodo: string): string {
  const hoy = new Date();
  const local = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
  return local === periodo ? `${local}-${String(hoy.getDate()).padStart(2, '0')}` : `${periodo}-01`;
}

function ultimoDia(periodo: string): string {
  const [anio, mes] = periodo.split('-').map(Number);
  const dias = new Date(anio!, mes!, 0).getDate();
  return `${periodo}-${String(dias).padStart(2, '0')}`;
}

const ENTRADA =
  'h-10 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors focus:border-marca-400';
